import { useEffect, useMemo, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { format } from "date-fns";
import {
    Calendar as CalendarIcon,
    CheckCircle2,
    ShieldCheck,
    Loader2,
    Minus,
    Plus,
    Truck,
    MapPin,
    Anchor,
    AlertTriangle,
    Award,
    X as XIcon,
} from "lucide-react";

import { Calendar } from "@/components/ui/calendar";
import {
    Popover,
    PopoverContent,
    PopoverTrigger,
} from "@/components/ui/popover";
import {
    Select,
    SelectContent,
    SelectItem,
    SelectTrigger,
    SelectValue,
} from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
    checkAvailability,
    createCheckout,
    getQuote,
    getScooter,
    validateCoupon,
} from "@/lib/api";

const DELIVERY_LOCATIONS = [
    { value: "orlando", label: "Orlando (FREE)", fee: 0 },
    { value: "kissimmee", label: "Kissimmee (+$25)", fee: 25 },
    {
        value: "port-canaveral",
        label: "Port Canaveral — Concierge Delivery",
        fee: 99,
    },
];
const PORT_CANAVERAL_MIN_DAYS = 4;

const CRUISE_LINES = [
    "Carnival Cruise Line",
    "Disney Cruise Line",
    "Royal Caribbean",
    "MSC Cruises",
    "Norwegian Cruise Line",
];

function isoDate(d) {
    return format(d, "yyyy-MM-dd");
}

export default function ScooterDetail() {
    const { id } = useParams();
    const [scooter, setScooter] = useState(null);
    const [loading, setLoading] = useState(true);

    // Booking state
    const [range, setRange] = useState({ from: undefined, to: undefined });
    const [quantity, setQuantity] = useState(1);
    const [delivery, setDelivery] = useState("delivery"); // 'delivery' | 'pickup'
    const [deliveryLocation, setDeliveryLocation] = useState("orlando");
    const [deliveryAddress, setDeliveryAddress] = useState("");
    const [name, setName] = useState("");
    const [email, setEmail] = useState("");
    const [phone, setPhone] = useState("");
    const [cruiseLine, setCruiseLine] = useState("none");
    const [notes, setNotes] = useState("");
    const [couponInput, setCouponInput] = useState("");
    const [couponApplied, setCouponApplied] = useState(null);
    const [couponError, setCouponError] = useState("");
    const [couponLoading, setCouponLoading] = useState(false);

    // Derived
    const [quote, setQuote] = useState(null);
    const [availability, setAvailability] = useState(null);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        setLoading(true);
        getScooter(id)
            .then(setScooter)
            .catch(() => toast.error("Could not load scooter."))
            .finally(() => setLoading(false));
    }, [id]);

    const datesReady = Boolean(range.from && range.to);

    // Fetch availability + quote when inputs change
    useEffect(() => {
        if (!datesReady || !scooter) return;
        const start = isoDate(range.from);
        const end = isoDate(range.to);
        checkAvailability({
            scooter_id: scooter.id,
            start_date: start,
            end_date: end,
            quantity,
        })
            .then(setAvailability)
            .catch(() => setAvailability(null));
        getQuote({
            scooter_id: scooter.id,
            start_date: start,
            end_date: end,
            quantity,
            delivery: delivery === "delivery",
            delivery_location: deliveryLocation,
        })
            .then(setQuote)
            .catch(() => setQuote(null));
    }, [range, quantity, delivery, deliveryLocation, scooter, datesReady]);

    const isConcierge =
        delivery === "delivery" && deliveryLocation === "port-canaveral";
    const conciergeViolation =
        isConcierge && quote && quote.days < PORT_CANAVERAL_MIN_DAYS;

    const canSubmit = useMemo(() => {
        return (
            datesReady &&
            availability?.available &&
            quote &&
            !conciergeViolation &&
            name.trim() &&
            email.trim() &&
            phone.trim() &&
            (delivery === "pickup" || (deliveryLocation && deliveryAddress.trim()))
        );
    }, [
        datesReady,
        availability,
        quote,
        conciergeViolation,
        name,
        email,
        phone,
        delivery,
        deliveryLocation,
        deliveryAddress,
    ]);

    async function handleBook() {
        if (!canSubmit || !scooter) return;
        setSubmitting(true);
        try {
            const payload = {
                scooter_id: scooter.id,
                start_date: isoDate(range.from),
                end_date: isoDate(range.to),
                quantity,
                delivery: delivery === "delivery",
                delivery_location:
                    delivery === "delivery" ? deliveryLocation : "pickup",
                delivery_address:
                    delivery === "delivery" ? deliveryAddress : null,
                customer_name: name,
                customer_email: email,
                customer_phone: phone,
                notes: [
                    cruiseLine && cruiseLine !== "none"
                        ? `Cruise line: ${cruiseLine}`
                        : null,
                    notes || null,
                ]
                    .filter(Boolean)
                    .join("\n") || null,
                coupon_code: couponApplied?.code || null,
                origin_url: window.location.origin,
            };
            const res = await createCheckout(payload);
            if (res.checkout_url) {
                // Break out of any iframe (Emergent preview) so Stripe can render.
                // Stripe sends X-Frame-Options: DENY, which blocks in-iframe loads.
                try {
                    if (window.top && window.top !== window.self) {
                        window.top.location.href = res.checkout_url;
                        return;
                    }
                } catch (_) {
                    // top may throw on cross-origin — fall through
                }
                window.location.href = res.checkout_url;
            } else {
                toast.error("Could not start checkout.");
            }
        } catch (err) {
            const msg =
                err?.response?.data?.detail ||
                "Could not create booking. Please try again.";
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    }

    if (loading) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-24 text-center text-slate-500">
                <Loader2 className="mx-auto h-6 w-6 animate-spin" />
            </div>
        );
    }
    if (!scooter) {
        return (
            <div className="mx-auto max-w-7xl px-4 py-24 text-center text-slate-500">
                Scooter not found.
            </div>
        );
    }

    return (
        <div
            data-testid="scooter-detail-page"
            className="mx-auto max-w-7xl px-4 pb-24 pt-12 sm:px-6 lg:px-8"
        >
            <div className="grid gap-12 lg:grid-cols-12">
                {/* LEFT: gallery + info */}
                <div className="lg:col-span-7">
                    <div className="overflow-hidden rounded-2xl border border-slate-200 bg-white">
                        <img
                            src={scooter.image}
                            alt={scooter.name}
                            className="aspect-[4/3] w-full object-cover"
                        />
                    </div>
                    <div className="mt-8">
                        <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                            Mobility Scooter
                        </div>
                        <h1 className="mt-2 font-display text-4xl font-extrabold tracking-tight text-[color:var(--brand-navy)] sm:text-5xl">
                            {scooter.name}
                        </h1>
                        <p className="mt-4 text-slate-600">
                            {scooter.description}
                        </p>
                        <ul className="mt-8 grid gap-3 sm:grid-cols-2">
                            {scooter.features.map((f) => (
                                <li
                                    key={f}
                                    className="flex items-start gap-2 text-sm text-slate-700"
                                >
                                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-green)]" />
                                    {f}
                                </li>
                            ))}
                        </ul>
                        <div className="mt-8">
                            <div className="mb-4 text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                                Rental Pricing
                            </div>
                            <div
                                data-testid="pricing-tiers-table"
                                className="card-surface overflow-hidden p-0"
                            >
                                <table className="w-full text-left">
                                    <thead className="bg-slate-50 text-xs uppercase tracking-widest text-slate-500">
                                        <tr>
                                            <th className="px-5 py-3 font-semibold">
                                                Rental Length
                                            </th>
                                            <th className="px-5 py-3 text-right font-semibold">
                                                Price
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="divide-y divide-slate-100 text-sm">
                                        {Object.entries(scooter.tiers)
                                            .sort(
                                                (a, b) =>
                                                    Number(a[0]) - Number(b[0]),
                                            )
                                            .map(([days, price]) => {
                                                const dayRate =
                                                    scooter.tiers["1"];
                                                const savings = Math.round(
                                                    Number(days) * dayRate -
                                                        Number(price),
                                                );
                                                return (
                                                    <tr
                                                        key={days}
                                                        data-testid={`tier-row-${days}`}
                                                        className="text-slate-700"
                                                    >
                                                        <td className="px-5 py-3 font-medium text-[color:var(--brand-navy)]">
                                                            {days}{" "}
                                                            {Number(days) === 1
                                                                ? "Day"
                                                                : "Days"}
                                                            {savings > 0 && (
                                                                <span
                                                                    data-testid={`tier-savings-${days}`}
                                                                    className="ml-2 inline-flex items-center rounded-full bg-[color:var(--brand-green)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white"
                                                                >
                                                                    Save $
                                                                    {savings}
                                                                </span>
                                                            )}
                                                        </td>
                                                        <td className="px-5 py-3 text-right font-display text-base font-extrabold text-[color:var(--brand-navy)]">
                                                            $
                                                            {Number(price).toFixed(0)}
                                                        </td>
                                                    </tr>
                                                );
                                            })}
                                    </tbody>
                                </table>
                            </div>
                            <p className="mt-3 text-xs text-slate-500">
                                Rentals between tiers are prorated
                                automatically. Beyond 30 days, additional days
                                are added at the 30-day marginal rate.
                            </p>
                        </div>

                        {/* FREE Delivery banner + zones */}
                        <div
                            data-testid="free-delivery-block"
                            className="mt-8 rounded-2xl border border-green-200 bg-green-50 p-6"
                        >
                            <div className="flex items-start gap-3">
                                <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-[color:var(--brand-green)] text-white">
                                    <Truck className="h-5 w-5" />
                                </span>
                                <div>
                                    <div className="text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green-2)]">
                                        Delivery Pricing
                                    </div>
                                    <h3 className="mt-1 font-display text-lg font-extrabold text-[color:var(--brand-navy)]">
                                        FREE Delivery to Disney, Universal
                                        &amp; Most Orlando Resorts
                                    </h3>
                                </div>
                            </div>
                            <ul className="mt-4 grid gap-2 sm:grid-cols-2">
                                {scooter.free_delivery_zones.map((z) => (
                                    <li
                                        key={z}
                                        data-testid={`free-zone-${z.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                        className="flex items-center gap-2 text-sm text-slate-700"
                                    >
                                        <span className="inline-flex items-center rounded-full bg-[color:var(--brand-green)] px-2 py-0.5 text-[10px] font-bold uppercase tracking-widest text-white">
                                            Free
                                        </span>
                                        {z}
                                    </li>
                                ))}
                            </ul>
                            <p className="mt-4 text-xs text-slate-500">
                                Kissimmee is included in our free delivery
                                area. Pickup at our Orlando office is always
                                free.
                            </p>
                        </div>

                        {/* Port Canaveral Concierge Delivery — premium card */}
                        <div
                            data-testid="port-canaveral-concierge-card"
                            className="mt-6 overflow-hidden rounded-2xl border border-[color:var(--brand-gold)]/40 bg-[color:var(--brand-navy)] p-6 text-white sm:p-8"
                        >
                            <div className="flex items-start justify-between gap-4">
                                <div>
                                    <div className="inline-flex items-center gap-2 rounded-full bg-[color:var(--brand-gold)]/20 px-3 py-1 text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                                        <Anchor className="h-3.5 w-3.5" />
                                        Concierge Service
                                    </div>
                                    <h3 className="mt-3 font-display text-2xl font-extrabold text-white">
                                        Port Canaveral Concierge Delivery
                                    </h3>
                                    <p className="mt-2 max-w-md text-sm leading-relaxed text-slate-300">
                                        White-glove round-trip service for
                                        cruise passengers.
                                    </p>
                                </div>
                                <div className="text-right">
                                    <div className="text-[10px] font-bold uppercase tracking-widest text-[color:var(--brand-gold)]">
                                        Flat Rate
                                    </div>
                                    <div className="font-display text-3xl font-extrabold text-white sm:text-4xl">
                                        $99
                                    </div>
                                </div>
                            </div>
                            <ul className="mt-6 grid gap-3 text-sm">
                                {[
                                    "Minimum 4-Day Rental Required",
                                    "Round-trip Delivery & Pickup",
                                    "Delivered directly to your cruise terminal or nearby hotel",
                                    "Convenient pickup after your cruise",
                                ].map((item) => (
                                    <li
                                        key={item}
                                        className="flex items-start gap-3 text-slate-200"
                                    >
                                        <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-[color:var(--brand-gold)]" />
                                        <span>{item}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    </div>
                </div>

                {/* RIGHT: booking widget */}
                <div className="lg:col-span-5">
                    <div
                        data-testid="booking-widget"
                        className="card-surface sticky top-24 p-6 sm:p-8"
                    >
                        <div className="mb-1 text-xs font-semibold uppercase tracking-widest text-[color:var(--brand-green)]">
                            Book This Scooter
                        </div>
                        <h2 className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]">
                            Reserve your dates
                        </h2>

                        {/* Dates */}
                        <div className="mt-6">
                            <Label className="mb-2 block text-sm font-medium text-slate-700">
                                Rental dates
                            </Label>
                            <Popover>
                                <PopoverTrigger asChild>
                                    <button
                                        type="button"
                                        data-testid="booking-date-trigger"
                                        className="flex w-full items-center gap-3 rounded-xl border border-slate-200 bg-white px-4 py-3 text-left text-sm text-slate-700 hover:border-slate-300"
                                    >
                                        <CalendarIcon className="h-4 w-4 text-[color:var(--brand-navy)]" />
                                        {range.from && range.to ? (
                                            <span>
                                                {format(range.from, "MMM d")} —{" "}
                                                {format(range.to, "MMM d, yyyy")}
                                            </span>
                                        ) : (
                                            <span className="text-slate-500">
                                                Select start & end dates
                                            </span>
                                        )}
                                    </button>
                                </PopoverTrigger>
                                <PopoverContent
                                    className="w-auto p-0"
                                    align="start"
                                >
                                    <Calendar
                                        mode="range"
                                        numberOfMonths={1}
                                        selected={range}
                                        onSelect={(v) =>
                                            setRange(v || { from: undefined, to: undefined })
                                        }
                                        disabled={{ before: new Date() }}
                                        data-testid="booking-calendar"
                                    />
                                </PopoverContent>
                            </Popover>
                        </div>

                        {/* Delivery / Pickup */}
                        <div className="mt-5">
                            <Label className="mb-2 block text-sm font-medium text-slate-700">
                                Delivery method
                            </Label>
                            <Select
                                value={delivery}
                                onValueChange={setDelivery}
                            >
                                <SelectTrigger
                                    data-testid="booking-delivery-select"
                                    className="h-12 rounded-xl"
                                >
                                    <SelectValue />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="delivery" data-testid="option-delivery">
                                        Delivery to me
                                    </SelectItem>
                                    <SelectItem value="pickup" data-testid="option-pickup">
                                        Pickup at our Orlando office
                                    </SelectItem>
                                </SelectContent>
                            </Select>
                        </div>

                        {delivery === "delivery" && (
                            <div className="mt-4 grid gap-4">
                                <div>
                                    <Label className="mb-2 block text-sm font-medium text-slate-700">
                                        Delivery city
                                    </Label>
                                    <Select
                                        value={deliveryLocation}
                                        onValueChange={setDeliveryLocation}
                                    >
                                        <SelectTrigger
                                            data-testid="booking-delivery-city-select"
                                            className="h-12 rounded-xl"
                                        >
                                            <SelectValue />
                                        </SelectTrigger>
                                        <SelectContent>
                                            {DELIVERY_LOCATIONS.map((l) => (
                                                <SelectItem
                                                    key={l.value}
                                                    value={l.value}
                                                    data-testid={`delivery-city-${l.value}`}
                                                >
                                                    {l.label}
                                                </SelectItem>
                                            ))}
                                        </SelectContent>
                                    </Select>
                                </div>
                                <div>
                                    <Label className="mb-2 block text-sm font-medium text-slate-700">
                                        Delivery address / hotel / terminal
                                    </Label>
                                    <Input
                                        data-testid="booking-address-input"
                                        placeholder="e.g. Hilton Orlando, 6001 Destination Pkwy"
                                        value={deliveryAddress}
                                        onChange={(e) =>
                                            setDeliveryAddress(e.target.value)
                                        }
                                        className="h-12 rounded-xl"
                                    />
                                </div>
                            </div>
                        )}

                        {/* Quantity */}
                        <div className="mt-5">
                            <Label className="mb-2 block text-sm font-medium text-slate-700">
                                Quantity
                            </Label>
                            <div className="flex items-center gap-2">
                                <button
                                    type="button"
                                    data-testid="booking-qty-decrement"
                                    onClick={() =>
                                        setQuantity((q) => Math.max(1, q - 1))
                                    }
                                    className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 hover:border-slate-300"
                                >
                                    <Minus className="h-4 w-4" />
                                </button>
                                <div
                                    data-testid="booking-qty-value"
                                    className="grid h-11 w-16 place-items-center rounded-xl border border-slate-200 bg-white font-display text-lg font-bold text-[color:var(--brand-navy)]"
                                >
                                    {quantity}
                                </div>
                                <button
                                    type="button"
                                    data-testid="booking-qty-increment"
                                    onClick={() =>
                                        setQuantity((q) => Math.min(10, q + 1))
                                    }
                                    className="grid h-11 w-11 place-items-center rounded-xl border border-slate-200 hover:border-slate-300"
                                >
                                    <Plus className="h-4 w-4" />
                                </button>
                            </div>
                        </div>

                        {/* Contact info */}
                        <div className="mt-6 grid gap-4">
                            <div>
                                <Label className="mb-2 block text-sm font-medium text-slate-700">
                                    Full name
                                </Label>
                                <Input
                                    data-testid="booking-name-input"
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    className="h-12 rounded-xl"
                                />
                            </div>
                            <div className="grid gap-4 sm:grid-cols-2">
                                <div>
                                    <Label className="mb-2 block text-sm font-medium text-slate-700">
                                        Email
                                    </Label>
                                    <Input
                                        data-testid="booking-email-input"
                                        type="email"
                                        value={email}
                                        onChange={(e) => setEmail(e.target.value)}
                                        className="h-12 rounded-xl"
                                    />
                                </div>
                                <div>
                                    <Label className="mb-2 block text-sm font-medium text-slate-700">
                                        Phone
                                    </Label>
                                    <Input
                                        data-testid="booking-phone-input"
                                        value={phone}
                                        onChange={(e) => setPhone(e.target.value)}
                                        className="h-12 rounded-xl"
                                    />
                                </div>
                            </div>
                            <div>
                                <Label className="mb-2 block text-sm font-medium text-slate-700">
                                    Cruise line (optional)
                                </Label>
                                <Select
                                    value={cruiseLine}
                                    onValueChange={setCruiseLine}
                                >
                                    <SelectTrigger
                                        data-testid="booking-cruise-line-select"
                                        className="h-12 rounded-xl"
                                    >
                                        <SelectValue placeholder="Choose your cruise line" />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem
                                            value="none"
                                            data-testid="cruise-line-none"
                                        >
                                            — Not cruising —
                                        </SelectItem>
                                        {CRUISE_LINES.map((line) => (
                                            <SelectItem
                                                key={line}
                                                value={line}
                                                data-testid={`cruise-line-${line.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                                            >
                                                {line}
                                            </SelectItem>
                                        ))}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label className="mb-2 block text-sm font-medium text-slate-700">
                                    Notes (optional)
                                </Label>
                                <Textarea
                                    data-testid="booking-notes-input"
                                    value={notes}
                                    onChange={(e) => setNotes(e.target.value)}
                                    placeholder="Ship name, arrival time, room #, etc."
                                    className="min-h-[80px] rounded-xl"
                                />
                            </div>
                        </div>

                        {/* Availability */}
                        {datesReady && availability && (
                            <div
                                data-testid="availability-status"
                                className={`mt-6 rounded-xl border p-4 text-sm ${
                                    availability.available
                                        ? "border-green-200 bg-green-50 text-green-800"
                                        : "border-red-200 bg-red-50 text-red-800"
                                }`}
                            >
                                {availability.available ? (
                                    <span>
                                        ✓ {availability.remaining} unit
                                        {availability.remaining === 1 ? "" : "s"}{" "}
                                        available for these dates
                                    </span>
                                ) : (
                                    <span>
                                        Sorry — sold out for these dates. Please
                                        pick different dates.
                                    </span>
                                )}
                            </div>
                        )}

                        {/* Concierge minimum-days warning */}
                        {conciergeViolation && (
                            <div
                                data-testid="concierge-min-days-warning"
                                className="mt-4 flex items-start gap-3 rounded-xl border border-amber-200 bg-amber-50 p-4 text-sm text-amber-900"
                            >
                                <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
                                <span>
                                    Port Canaveral Concierge Delivery requires
                                    a <strong>minimum 4-day rental</strong>.
                                    Please extend your dates or choose a
                                    different delivery city.
                                </span>
                            </div>
                        )}

                        {/* Pricing */}
                        {quote && (
                            <div
                                data-testid="pricing-summary"
                                className="mt-6 rounded-2xl bg-slate-50 p-5"
                            >
                                <div className="flex items-center justify-between text-sm text-slate-600">
                                    <span>
                                        {quote.days} day
                                        {quote.days === 1 ? "" : "s"} × {quote.quantity}{" "}
                                        scooter{quote.quantity === 1 ? "" : "s"}
                                    </span>
                                    <span
                                        data-testid="pricing-rental-subtotal"
                                        className="font-semibold text-[color:var(--brand-navy)]"
                                    >
                                        ${quote.rental_subtotal.toFixed(2)}
                                    </span>
                                </div>
                                <div className="mt-2 flex items-center justify-between text-sm text-slate-600">
                                    <span className="inline-flex items-center gap-2">
                                        {isConcierge ? (
                                            <Anchor className="h-4 w-4 text-[color:var(--brand-gold)]" />
                                        ) : (
                                            <Truck className="h-4 w-4" />
                                        )}
                                        {isConcierge
                                            ? "Port Canaveral Concierge"
                                            : quote.delivery_fee > 0
                                              ? "Delivery fee"
                                              : quote.delivery_fee === 0 &&
                                                  delivery === "delivery"
                                                ? "Delivery — FREE"
                                                : "Pickup — no fee"}
                                    </span>
                                    <span
                                        data-testid="pricing-delivery-fee"
                                        className={`font-semibold ${
                                            quote.delivery_fee === 0 &&
                                            delivery === "delivery"
                                                ? "text-[color:var(--brand-green-2)]"
                                                : "text-[color:var(--brand-navy)]"
                                        }`}
                                    >
                                        {quote.delivery_fee === 0 &&
                                        delivery === "delivery"
                                            ? "FREE"
                                            : `$${quote.delivery_fee.toFixed(2)}`}
                                    </span>
                                </div>
                                {couponApplied && (
                                    <div className="mt-2 flex items-center justify-between text-sm">
                                        <span className="inline-flex items-center gap-2 text-[color:var(--brand-green-2)]">
                                            <Award className="h-4 w-4" />
                                            Heroes Discount
                                        </span>
                                        <span
                                            data-testid="pricing-discount"
                                            className="font-semibold text-[color:var(--brand-green-2)]"
                                        >
                                            −${couponApplied.discount_amount.toFixed(2)}
                                        </span>
                                    </div>
                                )}
                                <div className="mt-4 flex items-center justify-between border-t border-slate-200 pt-4">
                                    <span className="font-display text-lg font-bold text-[color:var(--brand-navy)]">
                                        Total
                                    </span>
                                    <span
                                        data-testid="pricing-total"
                                        className="font-display text-2xl font-extrabold text-[color:var(--brand-navy)]"
                                    >
                                        $
                                        {Math.max(
                                            0,
                                            quote.total -
                                                (couponApplied?.discount_amount || 0),
                                        ).toFixed(2)}
                                    </span>
                                </div>
                            </div>
                        )}

                        {/* Heroes Discount code */}
                        <div className="mt-4">
                            {couponApplied ? (
                                <div
                                    data-testid="coupon-applied"
                                    className="flex items-center justify-between rounded-xl border border-[color:var(--brand-green)]/50 bg-green-50 p-3 text-sm"
                                >
                                    <span className="inline-flex items-center gap-2 font-semibold text-[color:var(--brand-green-2)]">
                                        <Award className="h-4 w-4" />
                                        {couponApplied.code} · $
                                        {couponApplied.discount_amount.toFixed(0)}{" "}
                                        off
                                    </span>
                                    <button
                                        type="button"
                                        onClick={() => {
                                            setCouponApplied(null);
                                            setCouponInput("");
                                        }}
                                        data-testid="coupon-remove"
                                        className="inline-flex h-8 w-8 items-center justify-center rounded-full text-slate-500 hover:bg-slate-100"
                                        aria-label="Remove discount code"
                                    >
                                        <XIcon className="h-4 w-4" />
                                    </button>
                                </div>
                            ) : (
                                <details className="rounded-xl border border-slate-200 bg-white">
                                    <summary
                                        data-testid="coupon-toggle"
                                        className="cursor-pointer list-none px-4 py-3 text-sm font-medium text-[color:var(--brand-navy)] hover:bg-slate-50"
                                    >
                                        Have a Heroes Discount code?
                                    </summary>
                                    <div className="border-t border-slate-100 p-3">
                                        <div className="flex gap-2">
                                            <input
                                                type="text"
                                                data-testid="coupon-input"
                                                value={couponInput}
                                                onChange={(e) => {
                                                    setCouponInput(
                                                        e.target.value.toUpperCase(),
                                                    );
                                                    setCouponError("");
                                                }}
                                                placeholder="HERO-XXXXXXXX"
                                                className="h-11 flex-1 rounded-xl border border-slate-200 bg-white px-3 font-mono text-sm uppercase text-[color:var(--brand-navy)] focus:border-[color:var(--brand-navy)] focus:outline-none"
                                            />
                                            <button
                                                type="button"
                                                data-testid="coupon-apply"
                                                disabled={
                                                    !couponInput.trim() ||
                                                    couponLoading
                                                }
                                                onClick={async () => {
                                                    setCouponLoading(true);
                                                    setCouponError("");
                                                    try {
                                                        const res =
                                                            await validateCoupon(
                                                                couponInput.trim(),
                                                            );
                                                        setCouponApplied(res);
                                                    } catch (err) {
                                                        setCouponError(
                                                            err?.response
                                                                ?.data
                                                                ?.detail ||
                                                                "Could not apply code",
                                                        );
                                                    } finally {
                                                        setCouponLoading(false);
                                                    }
                                                }}
                                                className="pill-btn inline-flex items-center gap-2 bg-[color:var(--brand-navy)] px-4 text-sm font-semibold text-white disabled:opacity-60"
                                            >
                                                {couponLoading ? (
                                                    <Loader2 className="h-4 w-4 animate-spin" />
                                                ) : (
                                                    "Apply"
                                                )}
                                            </button>
                                        </div>
                                        {couponError && (
                                            <p
                                                data-testid="coupon-error"
                                                className="mt-2 text-xs text-red-600"
                                            >
                                                {couponError}
                                            </p>
                                        )}
                                    </div>
                                </details>
                            )}
                        </div>

                        <button
                            type="button"
                            data-testid="booking-submit-button"
                            disabled={!canSubmit || submitting}
                            onClick={handleBook}
                            className="hero-cta pill-btn mt-6 inline-flex w-full items-center justify-center gap-2 text-white disabled:cursor-not-allowed disabled:opacity-60"
                        >
                            {submitting ? (
                                <>
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                    Preparing checkout…
                                </>
                            ) : (
                                <>Continue to Payment</>
                            )}
                        </button>

                        <div className="mt-4 flex items-center justify-center gap-2 text-xs text-slate-500">
                            <ShieldCheck className="h-4 w-4 text-[color:var(--brand-green)]" />
                            Secure checkout by Stripe · Cancel up to 48h
                            before delivery
                        </div>
                        <div className="mt-3 flex items-center justify-center gap-1 text-xs text-slate-500">
                            <MapPin className="h-3.5 w-3.5" />
                            Orlando · Kissimmee · Port Canaveral
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
